// Kennzahlen und Liste der Auftraege im Dokumenten-Assistenten (seit 21.10.2026).
// Die Kennzahlen stehen wie im alten Tool oben: Warteschlange, Aktiv,
// Abgeschlossen, Fehler (dazu zaehlen auch haengende Auftraege).

import React from 'react';
import { RefreshCw } from 'lucide-react';
import { T, karteStil, knopfStil } from '../../../lib/designTokens';
import { fehlerZeile, kennzahlen } from '../../../lib/dokumentenAssistent';
import { Ueberzeile } from '../abwaegenAnzeige';
import { Hinweis } from './AuftragDetails';
import AuftragKarte from './AuftragKarte';

function Kennzahl({ titel, wert, zeile, farbe = T.navy }) {
  return (
    <div style={{ ...karteStil, padding: '10px 12px', display: 'grid', gap: 2 }}>
      <Ueberzeile>{titel}</Ueberzeile>
      <div style={{ fontSize: 22, fontWeight: 700, color: wert ? farbe : T.faint, fontVariantNumeric: 'tabular-nums' }}>{wert}</div>
      <div style={{ fontSize: T.tMikro, color: T.muted }}>{zeile}</div>
    </div>
  );
}

function Kennzahlen({ auftraege }) {
  const z = kennzahlen(auftraege);
  return (
    <section aria-label="Kennzahlen" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
      <Kennzahl titel="Warteschlange" wert={z.warteschlange} zeile="warten auf den Start" />
      <Kennzahl titel="Aktiv" wert={z.aktiv} zeile="Seiten lesen oder ablegen" farbe={T.info} />
      <Kennzahl titel="Abgeschlossen" wert={z.abgeschlossen} zeile="fertig oder liegengelassen" farbe={T.gut} />
      <Kennzahl titel="Fehler" wert={z.fehler} farbe={T.gefahr} zeile={fehlerZeile(z)} />
    </section>
  );
}

/**
 * auftraege: neueste zuerst; geladen: erste Abfrage beendet; laedt: Abfrage laeuft;
 * fehler: Meldung, wenn die Liste nicht geladen werden konnte; aktionen: { [id]:
 * { art, fehler } } fuer "Erneut versuchen" und "Entfernen"; festeDealId: Reiter im Bitrix-Deal.
 */
export default function AuftragListe({
  auftraege, geladen, laedt, fehler, nachlaufEingerichtet, aktionen, onAktualisieren, onWiederholen, onEntfernen,
  festeDealId = '',
}) {
  const liste = Array.isArray(auftraege) ? auftraege : [];
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Kennzahlen auftraege={liste} />

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Ueberzeile>Aufträge</Ueberzeile>
          <div style={{ fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>
            {liste.length ? `${liste.length} ${liste.length === 1 ? 'Auftrag' : 'Aufträge'}` : 'Noch keine Aufträge'}
          </div>
        </div>
        <button type="button" onClick={onAktualisieren} disabled={laedt} style={{ ...knopfStil(false), opacity: laedt ? 0.6 : 1 }}>
          <RefreshCw size={12} style={laedt ? { animation: 'spin 1s linear infinite' } : undefined} />
          Aktualisieren
        </button>
      </div>

      {fehler ? <Hinweis ton="gefahr">{fehler}</Hinweis> : null}
      {nachlaufEingerichtet === false ? (
        <Hinweis ton="warn">
          Die Abarbeitung im Hintergrund ist auf diesem Server nicht eingerichtet. Aufträge können stehen bleiben –
          bitte einem Admin Bescheid geben.
        </Hinweis>
      ) : null}

      {!geladen ? (
        <div style={{ fontSize: T.tText, color: T.muted }}>Aufträge werden geladen …</div>
      ) : null}

      {geladen && liste.length === 0 && !fehler ? (
        <div style={{ ...karteStil, padding: '22px 16px', textAlign: 'center', fontSize: T.tText, color: T.muted, lineHeight: 1.6 }}>
          {festeDealId ? `Hier erscheinen die Aufträge der letzten 30 Tage für Deal ${festeDealId}.` : 'Hier erscheinen die Aufträge der letzten 30 Tage.'}
          <br />
          {festeDealId
            ? 'Oben Vertragsart und Datei wählen – der Assistent erledigt den Rest im Hintergrund, auch wenn dieser Reiter zu ist.'
            : 'Oben Deal und Datei wählen – der Assistent erledigt den Rest im Hintergrund, auch wenn diese Seite zu ist.'}
        </div>
      ) : null}

      {liste.map((auftrag) => (
        <AuftragKarte
          key={auftrag.id}
          auftrag={auftrag}
          aktion={aktionen?.[auftrag.id]?.art || ''}
          fehler={aktionen?.[auftrag.id]?.fehler || ''}
          onWiederholen={onWiederholen}
          onEntfernen={onEntfernen}
        />
      ))}
    </div>
  );
}
